import { useCallback } from 'react';
import {
  AskRequest,
  AskRequestCommentVisibilities,
  AskRequestCreateType,
  AskRequestCreateTypes,
} from '@hone-automation/common';
import { convertToShortcodes } from '../../utils/emojiParser';
import { mentionUtils } from '../mentionUtils';
import { transformAskRequestVisibility } from '../../utils/askRequestUtils';
import { AttachmentItemType } from '../Attachments/AttachmentContainer/AttachmentsContainer';
import { RichTextEditorRef } from '../RichTextEditor/RichTextEditor';

export type DestinationType = 'ALL' | 'INTERNAL' | 'EMAIL';

export interface UseComposerSubmitParams {
  // Content state
  text: string;
  richText: string;
  attachments: AttachmentItemType[];

  // Request context
  request?: AskRequest;
  selectedItem?: AskRequest | null;
  isClosedAndReopen: boolean;

  // User context
  userId?: string;
  locationId?: string;

  // Mode flags
  isEdit: boolean;
  sendTypeFinal: AskRequestCreateType;
  selectedDestination: DestinationType;
  shouldShowInternal: boolean;
  shouldShowEmailOption: boolean;

  // Callbacks
  onSave?: (content: string) => void;
  handleOnCallback?: (
    formData: FormData,
    createRequestType: AskRequestCreateType
  ) => void | false | Promise<void | false>;

  // Mutations
  createCommentMutation: (formData: FormData) => void;
  postReopenMutation: { mutate: (params: { trxId: string; comment: string }) => void };

  // State setters for reset
  resetFields: () => void;
  setKAIclicked: (value: boolean) => void;

  // Editor ref for submit action
  richTextEditorRef: React.RefObject<RichTextEditorRef>;
}

export interface UseComposerSubmitReturn {
  handleSubmit: (createAskRequestType: AskRequestCreateType) => void;
  handleSubmitWithDestination: () => void;
  allCommentsAreInternal: boolean;
}

/**
 * Hook that encapsulates all submission logic for the CMComposer
 */
export function useComposerSubmit({
  text,
  richText,
  attachments,
  request,
  selectedItem,
  isClosedAndReopen,
  userId,
  locationId,
  isEdit,
  sendTypeFinal,
  selectedDestination,
  shouldShowInternal,
  shouldShowEmailOption,
  onSave,
  handleOnCallback,
  createCommentMutation,
  postReopenMutation,
  resetFields,
  setKAIclicked,
  richTextEditorRef,
}: UseComposerSubmitParams): UseComposerSubmitReturn {
  const selectedItemFinal = selectedItem || request;

  const allCommentsAreInternal = request?.comments
    ? request.comments.every(comment => comment.visibility === AskRequestCommentVisibilities.Internal)
    : false;

  const setVisibility = useCallback((formData: FormData) => {
    const visibility = transformAskRequestVisibility(formData.get('createAskRequestType') as AskRequestCreateType);
    formData.set('visibility', visibility);
    formData.delete('createAskRequestType');
  }, []);

  const isContentEmpty = useCallback(() => {
    return text === '' || richText === '' || richText === '<br>';
  }, [text, richText]);

  const getFormattedContent = useCallback(() => {
    const emojiSafeRichText = convertToShortcodes(richText);
    return mentionUtils.convertHtmlMentionsToStorage(emojiSafeRichText);
  }, [richText]);

  const submitEditor = useCallback(() => {
    if (richTextEditorRef.current) {
      richTextEditorRef.current.submit();
    }
  }, [richTextEditorRef]);

  const handleEditSave = useCallback(() => {
    if (isContentEmpty()) return false;

    const storageFormattedText = getFormattedContent();
    onSave?.(storageFormattedText);
    submitEditor();
    setKAIclicked(false);
    return true;
  }, [isContentEmpty, getFormattedContent, onSave, submitEditor, setKAIclicked]);

  const handleReopenSubmit = useCallback(
    (storageFormattedText: string) => {
      if (!selectedItemFinal) return false;

      postReopenMutation.mutate({
        trxId: selectedItemFinal.id,
        comment: storageFormattedText,
      });
      resetFields();
      submitEditor();
      return true;
    },
    [selectedItemFinal, postReopenMutation, resetFields, submitEditor]
  );

  const handleCustomCallbackSubmit = useCallback(
    (formData: FormData, askRequestType: AskRequestCreateType) => {
      if (!handleOnCallback) return false;

      attachments.forEach(file => {
        formData.append('attachment', file.file as File);
      });

      const doReset = () => {
        resetFields();
        submitEditor();
      };

      const result = handleOnCallback(formData, askRequestType);

      if (result instanceof Promise) {
        result.then(value => {
          if (value !== false) doReset();
        });
      } else {
        if (result !== false) doReset();
      }

      return true;
    },
    [handleOnCallback, attachments, resetFields, submitEditor]
  );

  const handleMutationSubmit = useCallback(
    (formData: FormData) => {
      // Append attachments if any
      attachments.forEach(file => {
        formData.append('attachment', file.file as File);
      });

      setVisibility(formData);
      createCommentMutation(formData);
      submitEditor();
      resetFields();
    },
    [attachments, setVisibility, createCommentMutation, submitEditor, resetFields]
  );

  const buildFormData = useCallback(
    (storageFormattedText: string, askRequestType: AskRequestCreateType, shouldSendEmail: boolean) => {
      const formData = new FormData();

      formData.append('comment', storageFormattedText);
      formData.append('userId', userId as string);
      formData.append('locationId', locationId as string);
      formData.append('activeTransaction', JSON.stringify(request));
      formData.append('createAskRequestType', askRequestType);

      if (shouldSendEmail) {
        formData.append('sendEmail', JSON.stringify(true));
      }

      return formData;
    },
    [userId, locationId, request]
  );

  const handleSubmit = useCallback(
    (createAskRequestType: AskRequestCreateType) => {
      const askRequestType =
        allCommentsAreInternal && !isClosedAndReopen ? AskRequestCreateTypes.Internal : createAskRequestType;

      // Handle edit mode
      if (isEdit && onSave) {
        handleEditSave();
        return;
      }

      if (isContentEmpty()) return;

      const storageFormattedText = getFormattedContent();

      // Handle custom callback
      if (handleOnCallback) {
        const formData = buildFormData(storageFormattedText, askRequestType, false);
        handleCustomCallbackSubmit(formData, askRequestType);
        return;
      }

      // Handle reopen
      if (isClosedAndReopen) {
        handleReopenSubmit(storageFormattedText);
        return;
      }

      // Handle normal submission
      const formData = buildFormData(storageFormattedText, askRequestType, false);
      handleMutationSubmit(formData);
    },
    [
      allCommentsAreInternal,
      isClosedAndReopen,
      isEdit,
      onSave,
      handleEditSave,
      isContentEmpty,
      getFormattedContent,
      handleOnCallback,
      buildFormData,
      handleCustomCallbackSubmit,
      handleReopenSubmit,
      handleMutationSubmit,
    ]
  );

  const handleSubmitWithDestination = useCallback(() => {
    // Handle INTERNAL destination
    if (selectedDestination === 'INTERNAL' && shouldShowInternal) {
      handleSubmit('Internal');
      return;
    }

    // Handle ALL or EMAIL destination
    const shouldSendEmail = selectedDestination === 'EMAIL' && shouldShowEmailOption;

    // Handle edit mode
    if (isEdit && onSave) {
      handleEditSave();
      return;
    }

    if (isContentEmpty()) return;

    const storageFormattedText = getFormattedContent();

    // Handle custom callback
    if (handleOnCallback) {
      const formData = buildFormData(storageFormattedText, sendTypeFinal, shouldSendEmail);
      handleCustomCallbackSubmit(formData, sendTypeFinal);
      return;
    }

    // Handle reopen
    if (isClosedAndReopen) {
      handleReopenSubmit(storageFormattedText);
      return;
    }

    // Handle normal submission
    const formData = buildFormData(storageFormattedText, sendTypeFinal, shouldSendEmail);
    handleMutationSubmit(formData);
  }, [
    selectedDestination,
    shouldShowInternal,
    shouldShowEmailOption,
    isEdit,
    onSave,
    handleEditSave,
    isContentEmpty,
    getFormattedContent,
    handleOnCallback,
    buildFormData,
    sendTypeFinal,
    handleCustomCallbackSubmit,
    isClosedAndReopen,
    handleReopenSubmit,
    handleMutationSubmit,
    handleSubmit,
  ]);

  return {
    handleSubmit,
    handleSubmitWithDestination,
    allCommentsAreInternal,
  };
}
